import React from 'react';
import { MMMConfigFormState, MediaChannel, ADSTOCK_OPTIONS, SATURATION_OPTIONS, AdstockType, SaturationType } from '../../types/mmmConfig';
import { TextInput, Select, RadioGroup } from '../FormFields';

interface MediaChannelsSectionProps {
  config: MMMConfigFormState;
  dataColumns: string[];
  onChange: (config: Partial<MMMConfigFormState>) => void;
  errors: Record<string, string | undefined>;
}

export const MediaChannelsSection: React.FC<MediaChannelsSectionProps> = ({
  config,
  dataColumns,
  onChange,
  errors,
}) => {
  const handleAddChannel = () => {
    const newChannels = [...(config.media_channels || [])];
    newChannels.push({
      name: '',
      column: '',
      variable_column: null,
      adstock: 'geometric',
      saturation: 'hill',
    });
    onChange({ media_channels: newChannels });
  };

  const handleRemoveChannel = (index: number) => {
    const newChannels = (config.media_channels || []).filter((_, i) => i !== index);
    onChange({ media_channels: newChannels });
  };

  const handleChannelChange = <K extends keyof MediaChannel>(
    index: number,
    field: K,
    value: MediaChannel[K]
  ) => {
    const newChannels = [...(config.media_channels || [])];
    newChannels[index] = { ...newChannels[index], [field]: value };

    if (field === 'column' && value && !newChannels[index].name) {
      newChannels[index].name = value as string;
    }

    onChange({ media_channels: newChannels });
  };

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Media Channels</h2>
        <button onClick={handleAddChannel} className="btn btn-success text-sm">
          + Add Channel
        </button>
      </div>

      {(config.media_channels || []).length === 0 ? (
        <div className="py-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          No media channels added yet. Click "Add Channel" to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {(config.media_channels || []).map((channel, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-medium text-gray-800">Channel {index + 1}</h3>
                <button
                  onClick={() => handleRemoveChannel(index)}
                  className="btn btn-danger text-xs py-1 px-3"
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <TextInput
                  name={`channel_${index}_name`}
                  label="Channel Name"
                  value={channel.name}
                  onChange={(val) => handleChannelChange(index, 'name', val)}
                  placeholder="e.g., facebook_ads, google_search"
                  required
                  helperText="Alphanumeric and underscore only"
                  error={errors[`media_channels.${index}.name`]}
                />

                <Select
                  name={`channel_${index}_column`}
                  label="Spend Column"
                  value={channel.column}
                  onChange={(val) => handleChannelChange(index, 'column', val)}
                  options={dataColumns.map((col) => ({ value: col, label: col }))}
                  required
                  placeholder="Select spend column..."
                  error={errors[`media_channels.${index}.column`]}
                />
              </div>

              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <Select
                  name={`channel_${index}_variable_column`}
                  label="Impressions/Delivery Column (Optional)"
                  value={channel.variable_column || ''}
                  onChange={(val) => handleChannelChange(index, 'variable_column', val || null)}
                  options={[
                    { value: '', label: 'Same as spend' },
                    ...dataColumns.map((col) => ({ value: col, label: col }))
                  ]}
                  placeholder="Leave empty to use spend column"
                />
                <p className="text-xs text-blue-700 mt-2">
                  Specify impressions, clicks, or other delivery metrics if different from spend.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RadioGroup
                  name={`adstock_${index}`}
                  label="Adstock Type"
                  value={channel.adstock || 'geometric'}
                  onChange={(val) => handleChannelChange(index, 'adstock', val as AdstockType)}
                  options={ADSTOCK_OPTIONS}
                />

                <RadioGroup
                  name={`saturation_${index}`}
                  label="Saturation Type"
                  value={channel.saturation || 'hill'}
                  onChange={(val) => handleChannelChange(index, 'saturation', val as SaturationType)}
                  options={SATURATION_OPTIONS}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="info-box mt-6">
        <h3 className="info-title">Configuration Guide</h3>
        <ul>
          <li><strong>Spend Column:</strong> Your actual marketing spend. Maps to Robyn's paid_media_spends</li>
          <li><strong>Impressions Column:</strong> Optional delivery metrics. Maps to Robyn's paid_media_vars</li>
          <li>Geometric adstock is recommended for most marketing channels</li>
          <li>Hill saturation captures diminishing returns in advertising</li>
        </ul>
      </div>
    </div>
  );
};
